import { useState } from "react";

interface SquarePos {
  id: number;
  x: number;
  y: number;
  delay: number;
}

// Grid bounds expressed in 40 px cells. 50 × 30 covers 2000 × 1200,
// enough for 1920 × 1080 monitors and 27" ultrawides without
// leaving the screen edges pulse-free. The static grid lines tile
// automatically via the SVG `<pattern>`, so they cover any
// viewport size; only the randomly placed pulses need explicit
// coordinate coverage. Cells beyond the viewport are clipped by
// the SVG with no layout cost.
const COLS = 50;
const ROWS = 30;
const SQUARE_COUNT = 18;

function pickSquares(): SquarePos[] {
  const out: SquarePos[] = [];
  const seen = new Set<string>();
  let attempts = 0;
  while (out.length < SQUARE_COUNT && attempts < 200) {
    attempts += 1;
    const x = Math.floor(Math.random() * COLS);
    const y = Math.floor(Math.random() * ROWS);
    const key = `${x},${y}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ id: out.length, x, y, delay: Math.random() * 4500 });
  }
  return out;
}

/**
 * Site-wide grid backdrop. Fixed-position SVG grid + 14 randomly
 * placed cells that pulse brand-tinted on a 4.5 s cycle with
 * randomised per-cell delays so the cells never strobe in unison.
 *
 * Inspired by Magic UI's `AnimatedGridPattern` (magicui.design).
 * Implemented inline — no new dependency. The pattern feels like
 * a chart background, not a glow, which suits the fintech-trading
 * aesthetic the marketing site is going for.
 *
 * Mounted ONCE per page at the page root. Position fixed at
 * `z-index: 0`; page content sits at `z-index: 1` so the grid
 * doesn't clip at section boundaries. Radial mask fades the
 * pattern at the edges. `aria-hidden="true"` — purely decorative.
 *
 * `prefers-reduced-motion: reduce` snaps every pulse off (the
 * grid stays visible as a static texture; only the cell
 * animations stop).
 */
export default function GridBackdrop(): JSX.Element {
  const [squares] = useState(() => pickSquares());
  return (
    <div className="grid-backdrop" aria-hidden="true">
      <svg width="100%" height="100%" preserveAspectRatio="none">
        <defs>
          <pattern id="gb-grid" width="40" height="40" patternUnits="userSpaceOnUse">
            <path d="M40 0H0V40" fill="none" stroke="currentColor" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill="url(#gb-grid)" />
        {squares.map((s) => (
          <rect
            key={s.id}
            x={s.x * 40}
            y={s.y * 40}
            width="40"
            height="40"
            className="grid-backdrop-pulse"
            style={{ animationDelay: `${s.delay}ms` }}
          />
        ))}
      </svg>
    </div>
  );
}
